<template>
  <div class="flinks">
    <AppSpinner
      v-if="loading"
      class="m-auto"
      :color="hexColor()"
    />
    <div class="flinks__iframe-container">
      <Encrypting v-show="showAnimation" />
      <iframe
        v-show="!loading && !showAnimation"
        class="flinks__iframe"
        :src="iframeSource"
        frameborder="0"
        height="555"
        width="100%"
        @load="handleLoad"
      />
    </div>
    <div
      v-if="flinksLoading"
      class="flinks__loading"
    >
      Hang tight, we are connecting to your bank account.
    </div>
  </div>
</template>

<script>
import AppSpinner from '@/components/common/spinners/AppSpinner'
import theme from '@/mixins/theme'
import tracking from '@/mixins/tracking'
import offers from '@/mixins/offers'
import Axios from 'axios'
import 'url-polyfill'
import qs from 'qs'
import Encrypting from '@/components/common/flinks/Encrypting'

export default {
  // Name
  name: 'FlinksIframe',

  // Components
  components: {
    AppSpinner,
    Encrypting
  },

  mixins: [
    theme,
    offers,
    tracking
  ],

  // Props
  props: {
    docsStandAlone: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'blue'
    },

    eventPrefix: {
      type: String,
      default: 'ploan'
    },

    instituitionId: {
      type: Number,
      default: undefined
    }
  },

  // Data
  data () {
    const queryParams = {
      theme: 'light',
      redirectUrl: 'https://www.springfinancial.ca/apply',
      innerRedirect: true,
      backEnable: true,
      daysOfTransactions: process.env.VUE_APP_FLINKS_DAYS_OF_TRANSACTIONS,
      institutionFilterEnable: true,
      demo: !!process.env.VUE_APP_FLINKS_DEMO_ENABLED,
      tag: this.eventPrefix,
      consentEnable: process.env.VUE_APP_FLINKS_CONSENT_ENABLED
    }

    return {
      apiToken: process.env.VUE_APP_API_TOKEN,
      loading: false,
      flinksLoading: false,
      institutionSelected: false,
      baseUrl: process.env.VUE_APP_FLINKS_BASE_URL,
      redirectParams: '?' + qs.stringify(queryParams),
      documentsUrl: process.env.VUE_APP_API_DOCUMENTS_URL,
      flinksFields: [
        'Flinks_Request_ID__c',
        'Request_Status__c',
        'Bank_institution__c'
      ],
      loginStartTime: undefined,
      bank: '',
      firstLoad: false,
      showAnimation: false
    }
  },

  // Computed
  computed: {
    // used for non-documents standalone flow only
    cduid () {
      if (this.docsStandAlone) return
      return this.$store.state.application.cduid
    },

    // used for documents standalone flow only
    oppId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.oppId
    },

    // used for documents standalone flow only
    accountId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.accountId
    },

    iframeSource () {
      if (this.instituitionId) return `${this.baseUrl}Credential/${this.instituitionId}${this.redirectParams}`
      return `${this.baseUrl}${this.redirectParams}`
    },

    isOffer () {
      return this.$store.getters['offers/isOffer']
    },

    sessionid () {
      return this.$store.state.application.sessionId
    },

    showEncryptionAnimation () {
      return this.$route.meta.showEncryptionAnimation
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    crm () {
      return this.$store.state.account.crm
    }

  },

  // Created
  created () {
    this.loading = true
    window.addEventListener('message', this.handleMessageEvent)
    window.addEventListener('touchstart', {}) // Fix for weird iOS iframe scroll / click bug
    this.firstLoad = true
  },

  // Before Destroy
  beforeDestroy () {
    window.removeEventListener('message', this.handleMessageEvent)
  },

  // Methods
  methods: {
    handleLoad () {
      this.loading = false
      this.$emit('loaded')
    },

    /**
     * Handle the Message event from the iframe
     * (Other sources can throw these messages too, such as Vue Dev tools)
     * If a requestId exists on the event, it calls this.submitFlinksInfo()
     * @param {MessageEvent} - event
     */
    handleMessageEvent (event, err) {
      const step = event.data.step
      const loginId = event.data.loginId
      const institution = event.data.institution || ''
      const isRedirect = step && step === 'REDIRECT'
      const flinksCode = event.data.flinksCode || ''
      const requestId = event.data.requestId || ''

      if (step === 'COMPONENT_BACK_SESSION' && !process.env.VUE_APP_DISABLE_FI_SELECTOR) {
        return this.$router.back()
      }

      this.handleDatalayer(step, institution, loginId, flinksCode)

      if (this.showEncryptionAnimation && step && step === 'INSTITUTION_SELECTED') {
        this.handleEncryptionAnimation()
      }

      if (step && step === 'COMPONENT_LOAD_INSTITUTION_SELECTOR') {
        this.$emit('bank-select')
      } else if (step && step === 'COMPONENT_LOAD_CREDENTIAL') {
        this.$emit('verify-bank')
      }

      if (loginId && isRedirect) {
        // hide flinks message if invalid credentials
        this.flinksLoading = false

        if (this.isOffer) {
          const eContract = this.$store.state.offers.opportunity.id
          this.$_offers_submitFlinksData(eContract, loginId, requestId, '')
          return
        }

        this.submitFlinksInfo(
          loginId,
          flinksCode,
          institution
        )
      }
    },

    /**
     * Handle Datalayer
     * Fire the dataLayer events for each 'step' in the flinks iframe
     * @param {String} - step
     * @param {String} - institution
     */
    handleDatalayer (step, instituition = '', loginId, flinksCode) {
      switch (step) {
        case 'COMPONENT_LOAD_INSTITUTION_SELECTOR':
          this.flinksLoading = false
          if (this.institutionSelected) {
            dataLayer.push({
              event: `spring_${this.eventPrefix}_flinks_back_to_institution_selector`
            })
            this.institutionSelected = false
          }
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_component_load_institution_selector`
          })
          break
        case 'INSTITUTION_SELECTED':
          this.institutionSelected = true
          this.bank = instituition
          dataLayer.push(
            {
              event: `spring_${this.eventPrefix}_flinks_financial_institution_selected`,
              'selected_financial_institution': instituition
            }
          )
          break
        case 'COMPONENT_LOAD_CREDENTIAL':
          this.flinksLoading = false
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_component_load_credential`
          })
          break
        case 'COMPONENT_LOAD_MFA':
          this.flinksLoading = false
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_component_load_mfa`
          })
          break
        case 'SUBMIT_MFA':
          this.flinksLoading = true
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_submit_mfa`
          })
          break
        case 'REDIRECT':
          this.flinksLoading = false
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_redirect`
          })
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_authentication_status`,
            'flinks_response_code': 'REDIRECT'
          })
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_bank_connected_success`
          })
          this.handleFlinksTiming('SUCCESS')
          break
        case 'SUBMIT_CREDENTIAL':
          this.flinksLoading = true
          this.loginStartTime = Math.round(Date.now() / 1000)
          dataLayer.push({
            event: `spring_${this.eventPrefix}_flinks_submit_credential`
          })
          if (this.eventPrefix === 'ploan') window.rudderanalytics.track('spring_ploan_flinks_submit_credential')
          break
        default:
      }

      const flinksErrorCodes = ['INVALID_LOGIN', 'DISABLED_LOGIN']
      const isInFlinksErrorCodes = flinksErrorCodes.includes(flinksCode)

      if (isInFlinksErrorCodes) {
        dataLayer.push({
          event: `spring_${this.eventPrefix}_flinks_authentication_status`,
          'flinks_response_code': flinksCode
        })
        dataLayer.push({
          event: `spring_${this.eventPrefix}_flinks_bank_connected_failure`
        })
        this.handleFlinksTiming(flinksCode)
      }

      if (flinksCode === 'INVALID_SECURITY_RESPONSE') {
        dataLayer.push({
          event: `spring_${this.eventPrefix}_flinks_mfa_invalid_security_response`
        })
      }
    },

    /**
     * Submit Flinks Info
     * Grab the info and make the request to the Api
     * @param {String} - loginId
     * @param {String} - requestId
     * @param {String} - requestStatus
     * @param {String} - bankInstitution
     * @emits verified
     */
    async submitFlinksInfo (loginId, requestStatus, bankInstitution) {
      try {
        let data = new FormData()
        let source
        data.append('Type__c', 'Bank Statements')
        data.append('Login_ID__c', loginId)
        data.append('Flinks_Request_ID__c', 'undefined')
        data.append('Request_Status__c', requestStatus)
        data.append('Bank_institution__c', bankInstitution)
        if (this.docsStandAlone) {
          data.append('Additional_Documents_Submitted__c', true)
          if (this.accountId) data.append('Account__c', this.accountId)
          if (this.oppId) data.append('Opportunity__c', this.oppId)
          source = 'Agent Triggered'
          data.append('crm', this.crm)
        } else {
          if (this.cduid) data.append('cduid', this.cduid)
          source = this.returningCustomer ? 'Returning Customer' : 'Initial Application'
        }
        data.append('Source__c', source)
        await Axios.request({
          method: 'POST',
          url: this.documentsUrl,
          data: data
        })
        if (loginId) {
          this.$emit('verified')
          window.rudderanalytics.track(`spring_${this.eventPrefix}_bankdetails_step_completed`,
            { bank: bankInstitution,
              lead_source: this.leadSource,
              device: this.deviceType,
              returning_customer: this.returningCustomer
            })
        }
      } catch (error) {
        console.warn(error)
        alert('Oops! Something went wrong. Please call 1-888-865-6402 to finish your application')
      }
    },

    handleFlinksTiming (flinksCode) {
      const loginEndTime = Math.round(Date.now() / 1000)
      const time = loginEndTime - this.loginStartTime
      const date = new Date()
      const timestamp =
      `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()} ${date.getUTCHours()}:${date.getUTCMinutes()}:${date.getUTCSeconds()}.${date.getUTCMilliseconds()}`
      const flinksObj = {
        event: 'spring_flinks_timing',
        flinks_login_time: time,
        response_code: flinksCode,
        session_id: this.sessionid,
        cduid: this.cduid,
        bank: this.bank,
        flinks_login_timestamp: timestamp
      }
      dataLayer.push(flinksObj)
    },

    handleEncryptionAnimation () {
      this.showAnimation = true
      setTimeout(() => {
        this.showAnimation = false
      }, 3000)
    }
  }
}
</script>

<style scoped lang="scss">
.flinks {
  @apply m-auto;

  max-width: 335px;

  &__loading {
    @apply text-center;

    color: #9d9a9a;
    margin-top: 15px;
  }

  &__iframe-container {
    @apply relative;

    -webkit-overflow-scrolling: touch;
  }

  &__iframe {
    @apply border-2 border-solid border-gray-300;
    @apply rounded-20;
  }
}
</style>
